/**
 * Family/Friends Pty group affordability.
 *
 * Per person it reuses calculateMaxLoan (the verbatim port of the consultant
 * app's Ooba methodology) so this calculator, the public Affordability
 * Calculator and the consultant tool can never disagree:
 *
 *   max instalment = gross − debt repayments − living expenses,
 *                    capped at 30% of gross (never negative)
 *   max bond       = reverse-PMT of that instalment at the rate and term
 *   purchase price = max bond + the person's deposit
 *
 * The group is the sum across people. Suggested shareholding is each
 * person's contribution (deposit + max bond) as a share of the group's,
 * rounded to 2 decimals and corrected so it totals exactly 100.
 */
import { calculateMaxLoan } from './affordabilityCalculator';

export interface GroupPersonInput {
  name: string;
  grossIncome: number;
  livingExpenses: number;
  debtRepayments: number;
  deposit: number;
}

export interface GroupPersonResult {
  name: string;
  maxBond: number;
  maxInstalment: number;
  purchasePrice: number;
  /** deposit + maxBond — the basis of the suggested split. */
  contribution: number;
}

export interface GroupResult {
  people: GroupPersonResult[];
  totalBond: number;
  totalInstalment: number;
  totalPurchasePrice: number;
  /** The best any single person could buy on their own. */
  bestSinglePurchasePrice: number;
  /** Suggested shareholding % per person (same order), totals 100. */
  suggestedSplit: number[];
}

const num = (v: number) => (typeof v === 'number' && isFinite(v) && v > 0 ? v : 0);

export function calculateGroupAffordability(
  inputs: GroupPersonInput[],
  interestRatePercent: number,
  termYears: number
): GroupResult {
  const people = inputs.map((p, i): GroupPersonResult => {
    const r = calculateMaxLoan({
      grossIncome: num(p.grossIncome),
      salaryDeductions: num(p.debtRepayments),
      monthlyExpenses: num(p.livingExpenses),
      interestRate: num(interestRatePercent) / 100,
      loanTermYears: num(termYears) || DEFAULT_TERM_YEARS,
    });
    const deposit = Math.round(num(p.deposit));
    return {
      name: p.name.trim() || `Person ${i + 1}`,
      maxBond: r.maxLoanAmount,
      maxInstalment: r.maxMonthlyPayment,
      purchasePrice: r.maxLoanAmount + deposit,
      contribution: r.maxLoanAmount + deposit,
    };
  });

  const totalBond = people.reduce((s, p) => s + p.maxBond, 0);
  const totalInstalment = people.reduce((s, p) => s + p.maxInstalment, 0);
  const totalPurchasePrice = people.reduce((s, p) => s + p.purchasePrice, 0);
  const bestSinglePurchasePrice = people.reduce((m, p) => Math.max(m, p.purchasePrice), 0);

  return {
    people,
    totalBond,
    totalInstalment,
    totalPurchasePrice,
    bestSinglePurchasePrice,
    suggestedSplit: proportionalSplit(people.map((p) => p.contribution)),
  };
}

/**
 * Percentages (2 decimals) proportional to the weights, summing to exactly
 * 100. All-zero weights → an equal split. The rounding remainder goes to the
 * largest weight.
 */
export function proportionalSplit(weights: number[]): number[] {
  const n = weights.length;
  if (n === 0) return [];
  const w = weights.map((x) => (isFinite(x) && x > 0 ? x : 0));
  const total = w.reduce((s, x) => s + x, 0);
  const raw = total > 0 ? w.map((x) => (x / total) * 100) : w.map(() => 100 / n);
  const cents = raw.map((x) => Math.round(x * 100));
  const diff = 10000 - cents.reduce((s, x) => s + x, 0);
  if (diff !== 0) {
    let idx = 0;
    for (let i = 1; i < n; i++) if (raw[i] > raw[idx]) idx = i;
    cents[idx] += diff;
  }
  return cents.map((c) => c / 100);
}

/** "R 1 234 567" — rand, space thousands separators, no cents. */
export function formatRand(amount: number): string {
  const v = Math.round(isFinite(amount) ? amount : 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}R ${String(Math.abs(v)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`;
}

/** Default rate = prime − 0.5% (house rule for every rate-defaulting surface). */
export const DEFAULT_RATE_OFFSET_FROM_PRIME = -0.5;
/** Bond term: always 30 years by default (Jaco 2026-10-07). */
export const DEFAULT_TERM_YEARS = 30;
/**
 * Default deposit for ONE property (Jaco 2026-10-07): R30 000, shared across
 * the group — not R30 000 per person.
 */
export const DEFAULT_PROPERTY_DEPOSIT = 30_000;

/** Each person's default share of the property deposit (whole rands; the first person absorbs the rounding). */
export function defaultDepositShares(people: number): number[] {
  if (people <= 0) return [];
  const each = Math.floor(DEFAULT_PROPERTY_DEPOSIT / people);
  const shares = Array.from({ length: people }, () => each);
  shares[0] += DEFAULT_PROPERTY_DEPOSIT - each * people;
  return shares;
}
export const FALLBACK_PRIME = 10.5;
